'use client';

import * as React from 'react';

import { Label } from '@/hen-ui/atomics/Label';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import type { ChartSurfaceState } from '@/hen-ui/composites/ChartFrame/chart-surface.types';

/**
 * Default status copy per non-ready surface. Overridable, because the right
 * words for "nothing came back" depend on what the field lists.
 */
const DEFAULT_STATUS_MESSAGE: Readonly<
  Record<Exclude<ChartSurfaceState, 'ready'>, string>
> = {
  loading: 'Loading…',
  queryError: 'Failed to load',
  empty: 'No options available',
};

export interface FormFieldProps {
  /** Text shown above the control. */
  label?: string;
  /**
   * `id` of the control this labels.
   *
   * Use for a control whose interactive element is labelable — a `Select` or
   * `MultiSelect` trigger is a `button`, so the association works and clicking
   * the label opens it.
   */
  htmlFor?: string;
  /**
   * `id` written onto the label element.
   *
   * Use for a control that is a `role="group"` of several elements — a
   * `ToggleGroup` — which `htmlFor` cannot address. Point the group's
   * `aria-labelledby` at this instead.
   */
  labelId?: string;
  /** Renders the required indicator the `Label` atomic already draws. */
  required?: boolean;
  /**
   * Lifecycle of whatever populates the control, using the same vocabulary as
   * `ChartFrame`. Anything other than `ready` adds a status line BELOW the
   * control rather than replacing it: a control that renders empty with no
   * explanation is the state this exists to remove, and hiding the control as
   * well removes the reader's only affordance.
   */
  surface?: ChartSurfaceState;
  /** Overrides the default copy for the current non-ready surface. */
  statusMessage?: string;
  /** Persistent guidance, shown regardless of surface. */
  helperText?: string;
  children?: React.ReactNode;
}

/**
 * FormField
 *
 * Label, control, and status line in one vertical stack.
 *
 * Exists because the three choice controls disagree about labelling: `Select`
 * carries a label inside its own two-line trigger, and `MultiSelect` and
 * `ToggleGroup` carry none at all. A surface that renders all three — a
 * dashboard filter bar — has to compose one anyway, and every surface that did
 * so composed it slightly differently.
 *
 * Status and helper copy are linked onto a single element child through
 * `aria-describedby` so a screen reader that focuses the control hears them.
 * Multiple children are left alone — there is no single control to describe.
 *
 * @example
 * <FormField label='County' htmlFor='county-filter' surface='loading'>
 *   <Select id='county-filter' placeholder='Select county' />
 * </FormField>
 *
 * @example
 * // A group of controls cannot be reached by `htmlFor`.
 * <FormField label='Region' labelId='region-label'>
 *   <ToggleGroup type='multiple' aria-labelledby='region-label'>…</ToggleGroup>
 * </FormField>
 */
export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      htmlFor,
      labelId,
      required,
      surface = 'ready',
      statusMessage,
      helperText,
      children,
    },
    ref,
  ) => {
    const statusId = React.useId();
    const helperId = React.useId();
    const status =
      surface === 'ready'
        ? undefined
        : (statusMessage ?? DEFAULT_STATUS_MESSAGE[surface]);

    const describedBy =
      [status ? statusId : undefined, helperText ? helperId : undefined]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <Flex ref={ref} direction='column' gap='xxs'>
        {label ? (
          <Label
            id={labelId}
            htmlFor={htmlFor}
            variant='primary'
            size='l1'
            required={required}
          >
            {label}
          </Label>
        ) : null}
        {describeControl(children, describedBy)}
        {status ? (
          <Text
            id={statusId}
            tag='span'
            variant='l2'
            role='status'
            color={surface === 'queryError' ? 'error' : 'subtext'}
          >
            {status}
          </Text>
        ) : null}
        {helperText ? (
          <Text id={helperId} tag='span' variant='l2' color='subtext'>
            {helperText}
          </Text>
        ) : null}
      </Flex>
    );
  },
);

FormField.displayName = 'FormField';

function describeControl(
  children: React.ReactNode,
  describedBy: string | undefined,
): React.ReactNode {
  if (!describedBy) return children;

  const only = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<{ 'aria-describedby'?: string }> =>
      React.isValidElement(child),
  );
  if (only.length !== 1) return children;

  const child = only[0];
  return React.cloneElement(child, {
    'aria-describedby': mergeDescribedBy(
      child.props['aria-describedby'],
      describedBy,
    ),
  });
}

function mergeDescribedBy(
  existing: string | undefined,
  next: string | undefined,
): string | undefined {
  const merged = [existing, next].filter(Boolean).join(' ');
  return merged.length > 0 ? merged : undefined;
}
